import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { useData } from '../contexts/DataContext';
import { useT, translateWorkType } from '../lib/i18n';
import { Search, Copy, Edit2, Trash2, Eye, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { displayNumber, isNumberPending } from '../lib/numbering';

const money = (n) => (Number(n) || 0).toFixed(2) + ' $';
const FILTERS = ['all', 'draft', 'pending', 'accepted', 'refused', 'expired'];

export default function History() {
  const { submissions, removeSubmission, duplicateSubmission, findInvoiceForSubmission } = useData();
  const { t, lang } = useT();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    let list = submissions;
    if (filter !== 'all') list = list.filter(s => s.status === filter);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(
        s =>
          (s.client_name || '').toLowerCase().includes(needle) ||
          (s.submission_number || '').toLowerCase().includes(needle) ||
          (s.work_type || '').toLowerCase().includes(needle)
      );
    }
    return list;
  }, [submissions, filter, q]);

  const onDelete = async (s) => {
    if (!window.confirm(`${t('hist.delete')} ${displayNumber(s.submission_number, t)} ?`)) return;
    await removeSubmission(s.id);
    toast.success(t('hist.delete') + ' ✓');
  };

  const onDuplicate = async (s) => {
    const copy = await duplicateSubmission(s.id);
    if (copy) {
      toast.success(t('hist.duplicate') + ' ✓');
      nav(`/submissions/${copy.id}`);
    }
  };

  return (
    <Layout title={t('hist.title')}>
      <h1 className="font-heading text-4xl font-black tracking-tighter mb-6">{t('hist.title')}</h1>

      <div className="relative mb-4">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
        <input
          data-testid="search-input"
          className="field !pl-12"
          placeholder={t('hist.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6 pb-1">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`shrink-0 h-10 px-4 rounded-full text-xs font-bold uppercase tracking-wider border-2 transition-all ${
              filter === f ? 'bg-[#7A1F2B] text-white border-[#7A1F2B]' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
            }`}
          >
            {f === 'all' ? t('hist.all') : t(`status.${f}`)}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card p-8 text-center text-gray-500" data-testid="empty-history">{t('hist.empty')}</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <div key={s.id} className="card p-4" data-testid={`history-row-${s.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`font-mono text-xs ${isNumberPending(s.submission_number) ? 'italic text-amber-600' : 'text-gray-500'}`}>
                      {displayNumber(s.submission_number, t, { short: true })}
                    </span>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="font-bold truncate">{s.client_name || '—'}</div>
                  <div className="text-xs text-gray-500 truncate">{translateWorkType(s.work_type, lang)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-black text-lg">{money(s.summary?.grandTotal)}</div>
                  <div className="text-[10px] text-gray-500">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5">
                <Link to={`/submissions/${s.id}`} className="btn-ghost flex-1 gap-1 text-xs" data-testid={`action-view-${s.id}`}>
                  <Eye size={14} /> {t('hist.view')}
                </Link>
                <Link to={`/submissions/${s.id}/edit`} className="btn-ghost flex-1 gap-1 text-xs" data-testid={`action-edit-${s.id}`}>
                  <Edit2 size={14} /> {t('hist.edit')}
                </Link>
                <button onClick={() => onDuplicate(s)} className="btn-ghost flex-1 gap-1 text-xs" data-testid={`action-duplicate-${s.id}`}>
                  <Copy size={14} /> {t('hist.duplicate')}
                </button>
                <button onClick={() => onDelete(s)} className="btn-ghost flex-1 gap-1 text-xs !text-red-700 hover:!bg-red-50" data-testid={`action-delete-${s.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              {s.status === 'accepted' && (() => {
                const inv = findInvoiceForSubmission(s.id);
                return (
                  <div className="mt-1.5">
                    <Link
                      to={inv ? `/invoices/${inv.id}` : `/submissions/${s.id}`}
                      className="btn-ghost w-full gap-1.5 text-xs justify-center"
                      data-testid={`action-invoice-${s.id}`}
                    >
                      <Receipt size={14} />
                      {inv ? t('invoice.view') : t('invoice.convert')}
                    </Link>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
